import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInvalidateOnAdvancingActivity } from "advancing/activity/useInvalidateOnAdvancingActivity";
import { AdvancingDeclinedText } from "advancing/components/AdvancingDeclinedText";
import { AdvancingTravelPanel } from "advancing/components/AdvancingTravelPanel";
import { ReadonlyAdvancingValue } from "advancing/components/ReadonlyAdvancingValue";
import { AdvancingChatPanel } from "advancing/messages/AdvancingChatPanel";
import { getAdvancingRowValue, isEmptyAdvancingValue, toAdvancingInputValue } from "advancing/utils";
import { PhosphorCheckCircle, PhosphorPaperPlaneTilt } from "components/Atoms/PhosphorIcon";
import BrandLogo from "components/BrandLogo";
import { AdvancingProvideSectionTemplate } from "components/MainSectionTemplate/AdvancingProvideSectionTemplate";
import { getInPageEditFullWidth } from "components/Freefields/InPageEdit/FFInPageEditInput";
import TwoColumnLayout from "layout/TwoColumnLayout";
import React, { useState } from "react";
import { useLoaderData, useParams } from "react-router";
import { useCustomQuery } from "utilities/hookutils";
import { AdvancingValueInput } from "../components/AdvancingValueInput";
import { getProvideSidePanelQuery, isSameAdvancingValue } from "../utils";

interface ProvideRowState {
  // Only set while the value differs from the server value
  editedValue?: string;
  hasInteracted: boolean;
  isSaving: boolean;
}

const provideMutationKey = ["AdvancingProvide.ProvideFormRow"];

const getRowInputValue = (row: server.dto.AdvancingFormRow) => toAdvancingInputValue(row.formElementType, getAdvancingRowValue(row));

export const CheckAndProvideTab = () => {
  const { secret } = useParams();
  const viewModel = useLoaderData<server.viewmodel.AdvancingProvideFrontModel>();
  const sidePanelQuery = useCustomQuery(getProvideSidePanelQuery(secret));

  useInvalidateOnAdvancingActivity(viewModel.advancingSession.advancingSessionId, getProvideSidePanelQuery(secret).queryKey, { skipWhileMutating: provideMutationKey });

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      {sidePanelQuery.data
        ? <CheckAndProvideForm secret={secret} brandName={viewModel.advancingSession.brand.name} formGroups={sidePanelQuery.data.data.formGroups} />
        : <AdvancingProvideSectionTemplate isLoading />}
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn size="medium" gap="bigger">
      <BrandLogo brand={viewModel.advancingSession.brand} />
      <AdvancingTravelPanel isLoading={sidePanelQuery.isLoading} travelAppointments={sidePanelQuery.data?.data.travelAppointments ?? []} />
      <AdvancingChatPanel advancingSessionId={viewModel.advancingSession.advancingSessionId} incomingMessageType="fromAgent" />
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>;
};

interface CheckAndProvideFormProps {
  secret: string
  brandName: string
  formGroups: server.dto.AdvancingFormGroup<server.dto.AdvancingFormRow>[]
}

const CheckAndProvideForm = (props: CheckAndProvideFormProps) => {
  const queryClient = useQueryClient();
  const rows = props.formGroups.flatMap(group => group.rows);
  const [rowStates, setRowStates] = useState<Record<string, ProvideRowState>>(() => Object.fromEntries(
    rows.map(row => [row.advancingFormItemId.toString(), { hasInteracted: false, isSaving: false }])
  ));

  const updateRowState = (key: string, changes: Partial<ProvideRowState>) => {
    setRowStates(prev => ({ ...prev, [key]: { ...prev[key], ...changes } }));
  };

  const provideMutation = useMutation({
    mutationKey: provideMutationKey,
    mutationFn: (instruction: server.dto.ProvideAdvancingFormItemInstruction) => window.CoreApi.AdvancingProvide.ProvideFormRow(instruction, { toastOnError: true }).then(r => r.data),
    onMutate: instruction => {
      updateRowState(instruction.advancingFormItemId.toString(), { isSaving: true });
    },
    onSuccess: async (response, instruction) => {
      if (response.hasErrors) return;

      // Waits for the refetch so the old server value does not flash
      await queryClient.invalidateQueries({ queryKey: getProvideSidePanelQuery(props.secret).queryKey });
      updateRowState(instruction.advancingFormItemId.toString(), { editedValue: undefined });
    },
    onSettled: (_response, _error, instruction) => {
      updateRowState(instruction.advancingFormItemId.toString(), { isSaving: false });
    }
  });

  const onValueChanged = (key: string, value: string) => {
    const row = rows.find(row => row.advancingFormItemId.toString() === key);
    // Cleared date pickers give undefined. Cancelling an edit passes the initial value back
    const editedValue = value ?? "";
    updateRowState(key, { editedValue: isSameAdvancingValue(row.formElementType, editedValue, getRowInputValue(row)) ? undefined : editedValue });
  };

  const onEditStarted = (key: string) => {
    updateRowState(key, { hasInteracted: true });
  };

  return <AdvancingProvideSectionTemplate
    sections={props.formGroups.map((group, idx) => ({
      key: `${idx}_${group.label}`,
      title: group.label,
      fields: group.rows.map(row => {
        const key = row.advancingFormItemId.toString();
        const rowState = rowStates[key];
        const value = rowState.editedValue ?? getRowInputValue(row);
        const serverValue = getAdvancingRowValue(row);
        const isEditable = row.formElementBehavior !== "disabled";
        const isMandatory = row.formElementBehavior === "visibleMandatory";
        const isChangedValue = (v: string) => !isSameAdvancingValue(row.formElementType, v, getRowInputValue(row));
        const canSendValue = (v: string) => {
          if (!isEditable || isEmptyAdvancingValue(row.formElementType, v)) return false;
          // Rows that were not provided yet can be confirmed, the other phases only send changes
          return row.formItemPhase === "unknown" ? isMandatory || rowState.hasInteracted : isChangedValue(v);
        };

        const send = (v: string) => {
          // The button stays clickable while loading
          if (rowState.isSaving) return;
          provideMutation.mutate({ advancingFormItemId: row.advancingFormItemId, secret: props.secret, value: v });
        };

        const isChanged = isChangedValue(value);
        const isEmpty = isEmptyAdvancingValue(row.formElementType, value);

        const getFooter = () => {
          if (rowState.isSaving) return undefined;
          if (row.formItemPhase === "declined") return <AdvancingDeclinedText row={row} />;
          if (row.formItemPhase === "pending") return <div className="text-sublabel">Awaiting response from {props.brandName}</div>;
          if (row.formItemPhase === "unknown" && isMandatory) return <div className="text-sublabel">{isEmpty ? "Please provide a value" : "Please confirm or change this value"}</div>;
          return undefined;
        };

        return {
          key,
          label: row.label,
          sublabel: row.sublabel,
          phase: row.formItemPhase,
          formElementBehavior: row.formElementBehavior,
          actorName: row.actorName,
          lastModifiedUtc: row.lastModifiedUtc,
          isEmpty,
          render: () => <ReadonlyAdvancingValue type={row.formElementType} value={value} serverValue={serverValue} dropdownOptions={row.dropdownOptions} />,
          inPageEdit: isEditable ? {
            initialValue: value,
            fullWidth: getInPageEditFullWidth(row.formElementType),
            render: options => <AdvancingValueInput
              type={row.formElementType}
              dropdownOptions={row.dropdownOptions}
              maxLength={row.formElementMaxLength}
              reference={key}
              value={options.value}
              serverValue={serverValue}
              onChange={options.onChange}
              onEnter={options.enter}
              cancel={options.cancel}
            />,
          } : undefined,
          button: {
            type: isChanged ? "tertiary" : "primary",
            icon: isChanged ? PhosphorPaperPlaneTilt : PhosphorCheckCircle,
            isVisible: canSendValue(value),
            isLoading: rowState.isSaving,
            onClick: () => send(value),
          },
          // Enter does what the button would do for the entered value
          onEnter: v => {
            if (canSendValue(v)) send(v);
          },
          footer: getFooter(),
        };
      })
    }))}
    onValueChanged={onValueChanged}
    onEditStarted={onEditStarted}
  />;
};
